import flatten from 'lodash/flatten';
import { action, computed, observable, makeObservable } from 'mobx';

import type { LegacyDataParams, LegacyValueParams } from 'chaika-frontend-types/frontend-cms';

import { isTableExecute } from './constants';

import { LegacyData } from './LegacyData';

import { assertIsDefined } from '@utils/assertIsDefined';

const LEGACY_DATA_MESSAGE = 'Legacy data is read by the id of a rendered legacy block';

export class LegacyDataList {
    @observable
    private data: Map<string, LegacyData>;

    constructor(params: LegacyDataParams[][]) {
        makeObservable(this);

        this.data = new Map(
            flatten(params).map((legacyData, index) => [
                `${legacyData.label}_${index}`,
                new LegacyData({ id: `${legacyData.label}_${index}`, ...legacyData }),
            ]),
        );
    }

    @computed
    public get dataIds(): string[] {
        return Array.from(this.data.values())
            .filter(
                ({ tableName, blockValuesFieldNames }) =>
                    !isTableExecute(tableName) && blockValuesFieldNames.length > 0,
            )
            .map(({ id }) => id);
    }

    public getLegacyDataValueByFieldName(fieldName: string): LegacyValueParams & { dataId: string } {
        const block = this.getBlockByFieldName(fieldName);

        return block
            ? {
                  dataId: block.id,
                  ...block.getBlockValue(fieldName),
              }
            : ({} as any);
    }

    public hasSomeFieldData(fieldNames: string[]): boolean {
        return fieldNames.some(fieldName => this.hasFieldData(fieldName));
    }

    public hasFieldData(fieldName: string): boolean {
        const block = this.getBlockByFieldName(fieldName);

        return block ? block.hasFieldData(fieldName) : false;
    }

    public getFilteredDataIds(executeFieldNames: string[]): string[] {
        return Array.from(this.data.values())
            .filter(
                block =>
                    !isTableExecute(block.tableName) &&
                    block.getFilteredBlockValuesFieldNames(executeFieldNames).length > 0,
            )
            .map(({ id }) => id);
    }

    public getLegacyData(dataId: string): LegacyData {
        const legacyData = this.data.get(dataId);
        assertIsDefined(legacyData, LEGACY_DATA_MESSAGE);
        return legacyData;
    }

    public getLegacyDataValue(dataId: string, fieldName: string): LegacyValueParams {
        const data = this.data.get(dataId);
        assertIsDefined(data, LEGACY_DATA_MESSAGE);

        return data.getBlockValue(fieldName);
    }

    @action
    public updateLegacyDataValue(dataId: string, fieldName: string, value: string | number | boolean): void {
        const legacyData = this.data.get(dataId);
        assertIsDefined(legacyData, LEGACY_DATA_MESSAGE);
        legacyData.updateBlockValue(fieldName, value);
    }

    public serialize(): LegacyDataParams[][] {
        return Array.from(this.data.values()).map(data => [data.serialized]);
    }

    private getBlockByFieldName(fieldName: string): LegacyData | undefined {
        return Array.from(this.data.values()).find(block => block.hasField(fieldName));
    }
}
